import {Component, Input, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {Router} from '@angular/router';
import {TranslocoService} from '@jsverse/transloco';
import {Store} from '@ngrx/store';
import {GlobalService} from 'app/global/global.service';
import * as AuthActions from 'app/store/actions/auth.action';
import {AuthFacade} from 'app/store/auth.facade';
import {
    ApexAxisChartSeries,
    ApexChart,
    ApexDataLabels,
    ApexFill,
    ApexLegend,
    ApexPlotOptions,
    ApexResponsive,
    ApexStroke,
    ApexTooltip,
    ApexXAxis,
    ApexYAxis
} from 'ng-apexcharts';
import {combineLatest, debounceTime, startWith, Subject, Subscription, take, takeUntil} from 'rxjs';
import {filter} from 'rxjs/operators';
import {TranslationsHelperService} from '../../../../translations.helper.service';
import {IAdvanceFilter} from '../../../shared/advanced-filters/components/advance-filter';
import {ContractControlEmitterService} from '../../events/contract-control-emitter.service';
import {ContractControlAdvanceFilterParamType} from '../advanced-filters';
import {DefaultFilter} from '../contract-control-interfaces/default-filters.interface';
import {ContractControlService, IGraphDataUpcomingEvents} from '../contract-control.service';
import {ContractObservableService} from '../contract-observable.service';
import {ContractControlFacade} from '../store/facades/contract-control-facade';
import {UpComingGraphService} from './upcoming-graph.service';

export type ChartOptions = {
    series: ApexAxisChartSeries;
    chart: ApexChart;
    dataLabels: ApexDataLabels;
    stroke: ApexStroke;
    xaxis: ApexXAxis;
    yaxis: ApexYAxis;
    colors: string[];
    fill: ApexFill;
    legend: ApexLegend;
    responsive: ApexResponsive[];
    plotOptions: ApexPlotOptions;
    tooltip: ApexTooltip
};

export const DEFAULT_CONTRACT_CONTROL_FILTER_VALUE = 6;


@Component({
    selector: 'app-contract-control-graph',
    templateUrl: './contract-control-graph.component.html',
    styleUrls: ['./contract-control-graph.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class ContractControlGraphComponent implements OnInit, OnDestroy {
    @Input() contract_id: number = 0;
    @Input() status: 0 | 1;
    @Input() heading: string; //TODO: seems to be unused
    @Input() skeletonName: string;
    @Input() height: string;
    @Input() filterVisible: boolean = false;


    private _untilDestroyed$ = new Subject<void>();
    public incomingChart: Partial<ChartOptions> = {} as Partial<ChartOptions>;
    public filter:number = DEFAULT_CONTRACT_CONTROL_FILTER_VALUE;

    public graphData: IGraphDataUpcomingEvents[] = [];
    public eventFilters: DefaultFilter = {} as DefaultFilter;
    public isUpcomingGrpahLoader: boolean = false;
    public removeTooltip:  NodeJS.Timeout;
    public showTooltip = 'none';
    public tooltipOpacity = '0';
    public tooltipContent = '';
    public tooltipPosition = { top: '0px', left: '0px' };
    public tooltipTitle: string = '';
    public tooltipData;
    public selectedBarListing = [];
    private contractDataSubscription: Subscription;
    public _ccGraphFilter$ = this._authFacade$.getCCFilter('cc_upcoming_graph_filter');
    public destroy$ = new Subject<void>();
    private advancedFilters:  IAdvanceFilter<ContractControlAdvanceFilterParamType>[];

    /**
     *
     * @param upComingGraphService
     * @param contractControlEmitter
     * @param contractObservable
     * @param router
     * @param _contractControlService$
     * @param _store$
     * @param _authFacade$
     * @param _globalService$
     * @param _translocoService
     * @param _translationsHelperService
     * @param _contractControlFacade
     */
    constructor(
        private upComingGraphService: UpComingGraphService,
        private contractControlEmitter: ContractControlEmitterService,
        private contractObservable: ContractObservableService,
        private router: Router,
        private _contractControlService$: ContractControlService,
        private _store$: Store,
        private _authFacade$: AuthFacade,
        private _globalService$: GlobalService,
        private _translocoService: TranslocoService,
        private _translationsHelperService: TranslationsHelperService,
        private _contractControlFacade: ContractControlFacade,
    ) { }


    ngOnInit(): void {
        this._ccGraphFilter$.subscribe(result => {
            const value = Number(result?.value);
            this.filter = isNaN(value) || value === 0 ? DEFAULT_CONTRACT_CONTROL_FILTER_VALUE : value;
        });
        this.upComingGraphTrigger();
        this.getDefaultFiltersTrigger();

        if (!this.skeletonName)
            this.skeletonName = 'contract_control_Upcoming_column_chart';

    }

    /**
     * Get default filters trigger from EventEmitter
     */
    getDefaultFiltersTrigger() {
        combineLatest({
            defaultFilters: this.contractControlEmitter.getDefaultFilters().pipe(startWith(null)),
            advancedFilters: this._contractControlFacade.appliedAdvancedFilters$.pipe(filter(x=>x.every(y=>y.isApplied === true && y.isOpened === false)))
        })
            .pipe(
                debounceTime(500),
                takeUntil(this._untilDestroyed$)
            )
            .subscribe(({defaultFilters, advancedFilters}) => {
                this.eventFilters = defaultFilters;
                this.advancedFilters = advancedFilters;
                this.handleUpComingGraphRequest();
        });
    }


    /**
     * Fetch upcoming graph
     * 
     * @callback
     * @returns {void}
     * @memberof ContractControlGraphComponent
     * 
     * @description
     * This method listens for the `upComingGraphTrigger` event and fetches the upcoming graph data.
     * The method sets the loader state to true and checks if a contract ID is present.
     * If a contract ID is available, it fetches the upcoming graph data for the specific contract.
     * Otherwise, it fetches the upcoming graphs based on the provided filters.
     * The fetched data is then processed by the `performFormAction` method.
     * Additionally, if no contract ID is present, the current filter state is saved.
     * 
     * @see handleUpComingGraphRequest
     * @see saveFilter
     * @see performFormAction
     * @see saveFilter
     * @see fetchUpComingGraphForContract
     * @see fetchUpComingGraphs
     * @see getUpComingGraphTrigger
     * @see takeUntil
     * @see _untilDestroyed$
     */
    upComingGraphTrigger(): void {
        this.contractControlEmitter.getUpComingGraphTrigger().pipe(
            takeUntil(this._untilDestroyed$)
        ).subscribe(() => this.handleUpComingGraphRequest());
    }



    /**
     * Handles the request for upcoming graph data.
     * 
     * This method sets the loader state to true and checks if a contract ID is present.
     * If a contract ID is available, it fetches the upcoming graph data for the specific contract.
     * Otherwise, it fetches the upcoming graphs based on the provided filters.
     * 
     * The fetched data is then processed by the `performFormAction` method.
     * Additionally, if no contract ID is present, the current filter state is saved.
     * 
     * @returns {void}
     */
    handleUpComingGraphRequest(): void {
        this.isUpcomingGrpahLoader = true;
        if (this.contract_id != 0) {
            this.upComingGraphService.fetchUpComingGraphForContract(this.contract_id, this.filter).pipe(
                take(1)
            ).subscribe((response) => this.performFormAction(response));
        } else {
            this.upComingGraphService.fetchUpComingGraphs(this.filter, this.eventFilters, this.advancedFilters).pipe(
                take(1)
            ).subscribe((response) => {
                this.performFormAction(response);
                this.saveFilter();
            });

        }
    }



    /**
     * Handles the form action by processing the response data and initializing the graph.
     *
     * @param response - The response data containing upcoming event information.
     * @param response.graphData - The data for the graph.
     * @param response.months - The months to be displayed on the graph.
     * @param response.maxCount - The maximum count of events.
     */
    private performFormAction(response: IGraphDataUpcomingEvents): void {
        this.graphData = this.translateEventCategoriesNames(response['graphData']);
        this.initGraph(response['months'], response['maxCount']);
        this.isUpcomingGrpahLoader = false;
    }



    /**
     * Saves the current filter settings for the contract control graph.
     * 
     * This method subscribes to the `_ccGraphFilter$` observable and takes the first emitted value.
     * If the emitted value's `value` property matches the current filter, the method returns early.
     * Otherwise, it calls the `saveUpcomingGraphFilter` method of `_contractControlService$` to save the filter.
     * 
     * On successful save, it dispatches the `fetchUserSettings` action to update user settings.
     * If an error occurs during the save operation, it displays an error message using `toastrError`.
     */
    saveFilter(): void {
        this._ccGraphFilter$.pipe(
            take(1)
        ).subscribe(result => {
            if (result?.value == this.filter) return;
            this._contractControlService$.saveUpcomingGraphFilter({ id: result?.id, key: 'cc_upcoming_graph_filter', value: this.filter }).subscribe({
                next: () => this._store$.dispatch(AuthActions.fetchUserSettings()),
                error: (error: string) => this._globalService$.toastrError(error)
            });
        })

    }


    
    /**
     * Generates an array of month labels starting from the current month.
     *
     * @param filter - The number of months to generate labels for. Defaults to 12.
     * @returns An array of abbreviated month labels (e.g., ['Jan', 'Feb', 'Mar', ...]).
     */
    generateMonthsWithFilter(filter: number = 12): string[] {
        const currentMonth = new Date().getMonth() + 1;
        const dataPoints = [];
        for (let i = 0; i < filter; i++) {
            const monthIndex = currentMonth + i;
            const monthLabel = new Date(new Date().getFullYear(), monthIndex - 1, 1).toLocaleString('en-us', { month: 'short' });
            dataPoints.push(monthLabel);
        }
        return dataPoints;
    }




    /**
     * Applies a filter to the months displayed in the contract control graph.
     * If the provided filter value is the same as the current filter, the method returns without making changes.
     * Otherwise, it updates the filter and triggers a request to update the graph.
     *
     * @param {number} [filter=DEFAULT_CONTRACT_CONTROL_FILTER_VALUE] - The filter value to apply. Defaults to `DEFAULT_CONTRACT_CONTROL_FILTER_VALUE`.
     * @returns {void}
     */
    applyMonthsFilter(filter: number = DEFAULT_CONTRACT_CONTROL_FILTER_VALUE): void {
        if (this.filter == filter) return;
        this.filter = filter;
        this.handleUpComingGraphRequest()
    }



    /**
     * Navigates to the contract control view for the specified contract ID.
     *
     * @param id - The ID of the contract to view.
     */
    navigateToContractControl(id: number): void {
        this.router.navigateByUrl('contract-control/view/' + id)
    }

   


    /**
     * Handles the hover event on the chart and adjusts the tooltip visibility based on the hovered element.
     *
     * @param event - The mouse event triggered by hovering over the chart.
     * @param opacity - The opacity value to determine if the tooltip should be shown or hidden.
     */
    onChartHover(event: MouseEvent, opacity: number): void {
        const element = event.target as HTMLElement;
        const tagName = element.tagName;
        if (opacity == 1 && tagName == 'path') {
            this.onTooltipHover(1);
        }
        else {
            this.onTooltipHover(0);
        }
    }




    /**
     * Handles the hover event for a tooltip.
     * 
     * @param mouseOver - A number indicating the hover state. 
     *                    If 0, the tooltip will be hidden. 
     *                    Otherwise, any existing tooltip removal timeout will be cleared.
     */
    onTooltipHover(mouseOver: number): void {
        if (mouseOver == 0) {
            this.hideToolTip();
        }
        else {
            clearTimeout(this.removeTooltip);
        }
    }




   
    /**
     * Handles the click event on the chart.
     * 
     * This method is triggered when the chart is clicked. It checks if the 
     * `skeletonName` is 'contract_control_events_chart'. If so, it processes 
     * the `tooltipData` to extract unique contract IDs and their corresponding 
     * data, and stores them in `selectedBarListing`.
     * 
     * @remarks
     * - The method ensures that only unique contract IDs are added to the list.
     * - The `tooltipData` object is expected to have an `allData` property which 
     *   is an array containing data objects with an `id` property.
     * 
     * @example
     * ```typescript
     * // Assuming `tooltipData` is populated and `skeletonName` is set
     * this.onChartClick();
     * console.log(this.selectedBarListing); // Outputs the selected bar listing data
     * ```
     */
    onChartClick(): void {
        if (this.skeletonName == 'contract_control_events_chart') {
            const selectedBarListing = [];
            const contractIds = [];
            if ( Object.prototype.hasOwnProperty.call(this.tooltipData,'allData' )) {
                for (let i = 0; i < this.tooltipData.allData.length; i++) {
                    const contId = this.tooltipData.allData[i].data.id;
                    if (contractIds.indexOf(contId) == -1) {
                        contractIds.push(contId);
                        selectedBarListing.push(this.tooltipData.allData[i].data);
                    }
                }
            }
            this.selectedBarListing = selectedBarListing;
        }
    }




    /**
     * Hides the tooltip after a short delay.
     * 
     * This method clears any existing timeout for hiding the tooltip and sets a new timeout
     * to hide the tooltip after 200 milliseconds. The tooltip is hidden by setting the 
     * `showTooltip` property to 'none'.
     * 
     * @private
     */
    hideToolTip(): void {
        clearTimeout(this.removeTooltip);
        this.removeTooltip = setTimeout(() => {
            this.showTooltip = 'none';
        }, 200)
    }




    /**
     * Assigns content to a custom tooltip and positions it on the screen.
     *
     * @template T - The type of the data to be displayed in the tooltip.
     * @param {string} title - The title to be displayed in the tooltip.
     * @param {T} data - The data to be displayed in the tooltip.
     * @param {{ top: string, left: string }} rect - The position of the tooltip on the screen.
     * @returns {void}
     */
    assignTooltipContent<T = unknown>(title: string, data: T, rect: { top:string, left:string }): void {
        clearTimeout(this.removeTooltip);
        const tooltipBox = document.getElementById("custom-tooltip");
        tooltipBox.style.top = rect.top;
        tooltipBox.style.left = rect.left;
        this.tooltipTitle = title;
        this.tooltipData = data;
        this.showTooltip = 'block';
        setTimeout(() => {
            this.tooltipOpacity = '1';
        }, 50)
    }




    
    /**
     * Initializes the graph with the provided months and maximum count.
     *
     * @param {string[]} months - An array of month names to be displayed on the x-axis.
     * @param {number} maxCount - The maximum count value for the y-axis.
     *
     * @remarks
     * This method sets up the configuration for an ApexCharts bar chart, including
     * series data, chart type, plot options, data labels, tooltips, stroke, x-axis,
     * y-axis, legend, and fill options.
     *
     * The tooltip is customized to adjust its position and content dynamically.
     * The y-axis is configured to display rounded values and the x-axis labels are
     * translated using the `translateMonths` method.
     *
     * The chart colors, font styles, and other visual properties are also defined
     * within this method.
     */
    initGraph(months: string[], maxCount: number): void {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const parentComponent = this;
        this.incomingChart = {
            series: this.graphData as ApexAxisChartSeries,
            chart: {
                type: "bar",
                height: parentComponent.height,
                stacked: true,
                toolbar: {
                    show: false
                }
            },
            responsive: [
                {
                  breakpoint: 480,
                  options: {
                    legend: {
                      position: "bottom",
                      offsetX: -10,
                      offsetY: -10
                    }
                  }
                }
              ],
            plotOptions: {
                bar: {
                    columnWidth: '30%',
                }
            },
            dataLabels: {
                enabled: false,
                formatter: (val) => {
                    return Number(val) / 1000 + "";
                }
            },
            tooltip: {
                followCursor: false, // Prevents the tooltip from following the cursor
                enabled: true, // Enables the tooltip to be interactive
                style: {
                    fontSize: '12px',
                    fontFamily: 'Montserrat", Helvetica, Arial, serif'
                },
                custom: function ({
                    seriesIndex,
                    dataPointIndex,
                    w
                }) {
                    try {
                        const rect = document.querySelector('.apexcharts-tooltip')['style'];
                        const top = parseFloat(rect.top.replace('px', '')) + 20;
                        rect.top = top + 'px';
                        const left = parseFloat(rect.left.replace('px', '')) + 5;
                        rect.left = left + 'px';
                        const data = w.globals.initialSeries[seriesIndex].data[dataPointIndex];
                        const title = w.globals.initialSeries[seriesIndex].name;
                        parentComponent.assignTooltipContent(title, data, rect);
                        return '';
                    } catch (e) {
                        return '';
                    }
                }
            },
            stroke: {
                width: 1,
                colors: ["#fff"]
            },

            xaxis: {
                categories: this.translateMonths(months),
                labels: {
                    show: true,
                    style: {
                        fontSize: '12px',
                        fontFamily: 'Montserrat", Helvetica, Arial, serif',
                        fontWeight: 400,
                    },
                }
            },
            colors: ["#a2a1a1", "#a3d0fb", "#92c4f5", "#74b6f7"],
            yaxis: {
                min: 0, // Set the minimum value of the y-axis to 1
                max: 7, // Set the maximum value of the y-axis to 4 (or any desired maximum value)
                tickAmount: 7, // Specify the number of ticks (labels) you want on the y-axis
                labels: {
                    formatter: function (val) {
                        return `${Math.round(val)}`; // Round the value to the nearest whole number
                    },
                    show: true,
                    style: {
                        fontSize: '12px',
                        fontFamily: 'Montserrat", Helvetica, Arial, serif',
                        fontWeight: 400,
                    },
                }
            },
            legend: {
                show: true,
                position: "bottom",
                horizontalAlign: "center",
                fontSize: '12px',
                fontFamily: 'Montserrat", Helvetica, Arial, serif',
                fontWeight: 400,
            },
            fill: {
                opacity: 1
            }
        };
    }





    /**
     * Lifecycle hook that is called when the component is destroyed.
     * 
     * This method performs cleanup by unsubscribing from the contract data subscription
     * if it exists, and then completes the `_untilDestroyed$` subject to notify any
     * remaining subscriptions to clean up.
     */
    ngOnDestroy(): void {
        if (this.contractDataSubscription) {
            this.contractDataSubscription.unsubscribe();
        }
        this._untilDestroyed$.next();
        this._untilDestroyed$.complete();
    }


    
    /**
     * Translates an array of month shortcuts to their full translated names.
     *
     * @param months - An array of month shortcuts to be translated.
     * @returns An array of translated month names. If a translation fails, the original month shortcut is returned.
     */
    private translateMonths(months: string[]): string[] {
        return months.map(month => {
            try {
                return this._translationsHelperService.translateMonthByIndex(
                    this._translationsHelperService.matchMonthByShortcut(month),
                    true
                );
            } catch (error) {
                return month;
            }
        });
    }



    /**
     * Translates the event category names to the current language.
     *
     * @param responseElement - The array of event categories to translate.
     * @returns The translated event categories.
     */
    private translateEventCategoriesNames(responseElement) {
        responseElement.forEach((item) => {
            const translationPrefix = 'Dashboard - contract events - categories - ';
            const newName = this._translocoService.translate(`${translationPrefix}${item.name}`);
            item.name = newName?.startsWith(translationPrefix) ? item.name : newName;
        });


        return responseElement;
    }
}
